/* WebComponentStoreLocator component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Prop, Watch, h } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import WebComponentStoreLocatorTsx from './templates/cwc-store-locator';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import {
  StoreLocatorConfigData,
  StoreLocatorRequestData,
  StoreLocatorTextResources,
} from './config/StoreLocatorConfiguration';
import { DefaultConfigData, DefaultRequestData, DefaultTextResources } from './config/StoreLocatorDefaultTextResources';
import { TextResources, RequestData, ConfigData } from './config/StoreLocatorTextResources';
import { STORELOCATORCOMPONENT } from './components/storelocator.component';
import { NOMINATIM } from './services/nominatim.service';
import { URLSERVICE } from './services/url.service';
import { HELPER } from './services/helper.service';
import MobileDetect from 'mobile-detect';
import deepmerge from 'deepmerge';

@Component({
  tag: 'cwc-store-locator',
  styleUrls: [
    './styles/cwc-store-locator.css',
    './styles/single.location.css',
    './styles/cewe.fonts.css',
    './styles/google.font.css',
  ],
  shadow: true,
})
export class WebComponentStoreLocator {
  /**
   * element
   */
  @Element() element: HTMLElement;

  /**
   * TextResources object with translations.
   */
  @Prop({ attribute: 'text-resources' }) _textResources: StoreLocatorTextResources | string;
  /**
   * property / attribute to inject moduleConfig via markup.
   */
  @Prop({ attribute: 'config' }) _moduleConfig: StoreLocatorConfigData | string;

  /**
   * text resources for translations.
   * @private
   */
  private textResources: StoreLocatorTextResources = DefaultTextResources;
  /**
   * settings of the default config data.
   * @private
   */
  private moduleConfig: StoreLocatorConfigData = DefaultConfigData;
  /**
   * data for xtci request
   * @private
   */
  private requestDataXtci: StoreLocatorRequestData = DefaultRequestData;
  /**
   * user agent
   * @private
   */
  private mobileDetect = new MobileDetect(window.navigator.userAgent);

  /**
   * watch text-resource attribute and convert json string to object.
   * @param newValue
   */
  @Watch('_textResources')
  setTextResourcesConfig(newValue: StoreLocatorTextResources | string) {
    let passedConfig;

    if (typeof newValue === 'string') {
      try {
        passedConfig = JSON.parse(newValue);
      } catch (e) {
        console.error('invalid text ressources, using defaults', newValue, e);
      }
    } else {
      passedConfig = newValue;
    }

    this.textResources = deepmerge(DefaultTextResources, passedConfig ?? {});
    TextResources.resources = this.textResources;
  }

  /**
   * watch config attribute and convert json string to object.
   * @param newValue
   */
  @Watch('_moduleConfig')
  setModuleConfig(newValue: StoreLocatorConfigData | string) {
    if (typeof newValue === 'string') {
      this.moduleConfig = { ...this.moduleConfig, ...JSON.parse(newValue) };
    } else {
      this.moduleConfig = { ...this.moduleConfig, ...newValue };
    }

    this.changeXtciRequestData(
      this.moduleConfig.keyaccount,
      this.moduleConfig.countrycode,
      this.moduleConfig.onsiteprinter,
    );
    ConfigData.data = this.moduleConfig;

    if (this.mobileDetect) {
      ConfigData.data['deviceInfo'] = this.mobileDetect;
    }
  }

  /**
   * Updated request parameters
   * @param keyaccount
   * @param countrycode
   * @param onsiteprinter
   */
  changeXtciRequestData(keyaccount: number, countrycode: string, onsiteprinter: number) {
    this.requestDataXtci.init.keyacc_id = keyaccount;
    this.requestDataXtci.init.locale = countrycode;
    this.requestDataXtci.location.onsite_printer_only = onsiteprinter;

    RequestData.request = this.requestDataXtci;
  }

  /**
   * Set search parameters & update single location config
   */
  initStoreLocator() {
    const params = new URLSearchParams(document.location.search);
    const searchLocId = params.has('locid');
    const searchS = params.has('s');
    const searchCurrentCity = this.moduleConfig['currentCity'];
    let receiveParameters = false;
    let singleLocations = false;
    let searchValue = '';

    if (searchS || (searchCurrentCity && !searchLocId)) {
      receiveParameters = true;
      this.moduleConfig = { ...this.moduleConfig, ...{ receiveParameters } };
      ConfigData.data = this.moduleConfig;

      if (searchS) {
        searchValue = params.get('s');
        const countryCode = this.requestDataXtci.init.locale.substring(3);
        const requestParameter = `${'?q=' + searchValue + '&countrycodes=' + countryCode.toLowerCase() + '&limit=1'}`;
        STORELOCATORCOMPONENT.currentCity = HELPER.capitalizeFirstLetter(searchValue);

        NOMINATIM.executeRequest(requestParameter, (callback) => {
          if (callback.length > 0) {
            const callbackCoords = { lat: Number(callback[0].lat), lng: Number(callback[0].lon) };
            STORELOCATORCOMPONENT.geoCoords = callbackCoords;
            STORELOCATORCOMPONENT.setSearchParams(receiveParameters, singleLocations, '');
            STORELOCATORCOMPONENT.fetchLocationData('', false, callbackCoords);
          } else {
            STORELOCATORCOMPONENT.fetchLocationData('', false);
            STORELOCATORCOMPONENT.displayNoticeOverlay(TextResources.resources['error'].location);
            URLSERVICE.deleteParams();
          }
        });
      } else {
        const coords = { lat: Number(this.moduleConfig['latitude']), lng: Number(this.moduleConfig['longitude']) };
        STORELOCATORCOMPONENT.geoCoords = coords;
        STORELOCATORCOMPONENT.currentCity = HELPER.capitalizeFirstLetter(searchCurrentCity);
        STORELOCATORCOMPONENT.setSearchParams(receiveParameters, singleLocations, '');
        STORELOCATORCOMPONENT.fetchLocationData('', false, coords);
      }
    } else if (searchLocId) {
      receiveParameters = true;
      singleLocations = true;
      searchValue = params.get('locid');
      this.moduleConfig = { ...this.moduleConfig, ...{ singleLocations } };
      ConfigData.data = this.moduleConfig;
      STORELOCATORCOMPONENT.setSearchParams(receiveParameters, singleLocations, searchValue);
      STORELOCATORCOMPONENT.fetchLocationData(searchValue, searchLocId);
    } else {
      STORELOCATORCOMPONENT.fetchLocationData('', false);
    }
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It's important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {}

  /**
   * Called every time the component is disconnected from the DOM, ie, it call be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {
    this.setModuleConfig(this._moduleConfig);
    this.setTextResourcesConfig(this._textResources);
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    this.initStoreLocator();
  }

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It's never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It's never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * render
   */
  render() {
    return <WebComponentStoreLocatorTsx ref={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(WebComponentStoreLocator.prototype, AbstractComponent.prototype);
export interface WebComponentStoreLocator extends AbstractComponent {}
